import React from 'react';
import { formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { Grid } from 'onefaceui-react-components';
import PropTypes from 'prop-types';
import {
  BaseCrud,
  FormSection,
  FormSectionColumn
} from 'react/containers/layout/Crud';
import {
  LookupField,
  CheckboxField,
  RadioGroupField,
  TimeField
} from 'react/components/fields';
import { useGRID } from 'react/hooks/useGrid';

const renderGeneralSection = () => (
  <FormSection title="routeactionsconfiguration.general.title">
    <FormSectionColumn>
      <LookupField
        label="routeactionsconfiguration.organization"
        name="organization"
        lookupEntity="Organization"
        lookupEntityColumn="description"
        required
      />
      <Grid
        templateColumns="1fr 1fr"
        breakpoints={{ lg: { templateColumns: '1fr' } }}
      >
        <LookupField
          label="routeactionsconfiguration.equipment"
          name="equipments"
          lookupEntity="Equipment"
          lookupEntityColumn="key"
          multiple
        />

        <LookupField
          label="configuration.driver"
          name="drivers"
          lookupEntity="Driver"
          lookupEntityColumn="key"
          multiple
        />
      </Grid>
    </FormSectionColumn>
  </FormSection>
);

function renderCompoundRASConfigurationSection({
  allowStart,
  allowComplete,
  startBasedOn,
  completeBasedOn
}) {
  const TIME_FIELD_CONFIG = {
    hourMax: 3,
    hourGrid: 1,
    stepHour: 1,
    showButtonPanel: false,
    hour: 1
  };

  return (
    <FormSection title="routeactionsconfiguration.configuration.title">
      <FormSectionColumn>
        <CheckboxField
          label="routeactionsconfiguration.allowStart"
          name="allowStart"
        />
        {allowStart && (
          <>
            <RadioGroupField
              name="startBasedOn"
              label="routeactionsconfiguration.field.startBasedOn"
              shouldShow={allowStart}
              value={allowStart && !startBasedOn ? 'FIXED' : startBasedOn}
              defaultValue="FIXED"
              doNotShowOnGrid
              options={[
                {
                  label: 'routeactionsconfiguration.field.basedOn.fixed',
                  value: 'FIXED',
                  optionHint:
                    'routeactionsconfiguration.field.start.basedOn.hintFixed'
                },
                {
                  label: 'routeactionsconfiguration.field.basedOn.planned',
                  value: 'PLANNED',
                  optionHint:
                    'routeactionsconfiguration.field.start.basedOn.hintPlanned'
                },
                {
                  label: 'routeactionsconfiguration.field.basedOn.baseline',
                  value: 'BASELINE',
                  optionHint:
                    'routeactionsconfiguration.field.start.basedOn.hintBaseline'
                }
              ]}
            />
            {startBasedOn === 'FIXED' && (
              <TimeField
                label="routeactionsconfiguration.startAfterTimeSecs"
                name="startAfterTimeSecs"
                doNotShowOnGrid
                required
              />
            )}
            {startBasedOn === 'PLANNED' && (
              <TimeField
                label="routeactionsconfiguration.startAfterPlannedTimeSecs"
                name="startAfterPlannedTimeSecs"
                doNotShowOnGrid
                required
                {...TIME_FIELD_CONFIG}
              />
            )}
            {startBasedOn === 'BASELINE' && (
              <TimeField
                label="routeactionsconfiguration.startAfterBaseLineTimeSecs"
                name="startAfterBaseLineTimeSecs"
                doNotShowOnGrid
                required
                {...TIME_FIELD_CONFIG}
              />
            )}
          </>
        )}
      </FormSectionColumn>

      <FormSectionColumn>
        <CheckboxField
          label="routeactionsconfiguration.allowComplete"
          name="allowComplete"
        />
        {allowComplete && (
          <>
            <RadioGroupField
              name="completeBasedOn"
              label="routeactionsconfiguration.field.completeBasedOn"
              defaultValue="FIXED"
              shouldShow={allowComplete}
              value={!completeBasedOn ? 'FIXED' : completeBasedOn}
              doNotShowOnGrid
              options={[
                {
                  label: 'routeactionsconfiguration.field.basedOn.fixed',
                  value: 'FIXED',
                  optionHint:
                    'routeactionsconfiguration.field.complete.basedOn.hintFixed'
                },
                {
                  label: 'routeactionsconfiguration.field.basedOn.planned',
                  value: 'PLANNED',
                  optionHint:
                    'routeactionsconfiguration.field.complete.basedOn.hintPlanned'
                },
                {
                  label: 'routeactionsconfiguration.field.basedOn.baseline',
                  value: 'BASELINE',
                  optionHint:
                    'routeactionsconfiguration.field.complete.basedOn.hintBaseline'
                },
                {
                  label: 'routeactionsconfiguration.field.basedOn.laststop',
                  value: 'LAST_STOP',
                  optionHint:
                    'routeactionsconfiguration.field.complete.basedOn.hintLaststop'
                }
              ]}
            />
            {completeBasedOn === 'FIXED' && (
              <TimeField
                label="routeactionsconfiguration.completeAfterTimeSecs"
                name="completeAfterTimeSecs"
                doNotShowOnGrid
                required
              />
            )}
            {completeBasedOn === 'PLANNED' && (
              <TimeField
                label="routeactionsconfiguration.completeAfterPlannedTimeSecs"
                name="completeAfterPlannedTimeSecs"
                doNotShowOnGrid
                required
                {...TIME_FIELD_CONFIG}
              />
            )}
            {completeBasedOn === 'BASELINE' && (
              <TimeField
                label="routeactionsconfiguration.completeAfterBaseLineTimeSecs"
                name="completeAfterBaseLineTimeSecs"
                doNotShowOnGrid
                required
                {...TIME_FIELD_CONFIG}
              />
            )}
            {completeBasedOn !== 'LAST_STOP' && (
              <LookupField
                label="routeactionsconfiguration.cancelCode"
                name="cancelCode"
                lookupEntity="CancelCode"
                lookupEntityColumn="key"
                shouldShow={allowComplete}
                required
              />
            )}
          </>
        )}
      </FormSectionColumn>
    </FormSection>
  );
}

function RouteActionsConfigurationCrud(props) {
  const { getActualPage } = useGRID('RouteActionsConfiguration');

  const beforeList = () => {
    const routeActionsConfigurationForm = App.Forms.RouteActionsConfiguration;
    const actualPagePerDataLength =
      routeActionsConfigurationForm.dataProvider.data.length / 51;
    const actualFirstPage = routeActionsConfigurationForm.pagination.first;
    const nextPage = Math.ceil(getActualPage());

    App.Forms.RouteActionsConfiguration.pagination.first =
      actualPagePerDataLength < nextPage ? actualFirstPage : nextPage;
  };

  const beforeSubmit = (values) => {
    const { startBasedOn, completeBasedOn, allowComplete, allowStart } = values;

    const deleteFields = (fields) => {
      fields.forEach((field) => delete values?.[field]);
    };

    const deleteStartFields = () => {
      if (!allowStart) {
        deleteFields([
          'startAfterTimeSecs',
          'startAfterPlannedTimeSecs',
          'startAfterBaseLineTimeSecs'
        ]);
      } else {
        if (['PLANNED', 'BASELINE'].includes(startBasedOn)) {
          delete values?.startAfterTimeSecs;
        }
        if (['FIXED', 'BASELINE'].includes(startBasedOn)) {
          delete values?.startAfterPlannedTimeSecs;
        }
        if (['FIXED', 'PLANNED'].includes(startBasedOn)) {
          delete values?.startAfterBaseLineTimeSecs;
        }
      }
    };

    const deleteCompleteFields = () => {
      if (!allowComplete) {
        deleteFields([
          'completeAfterTimeSecs',
          'completeAfterPlannedTimeSecs',
          'completeAfterBaseLineTimeSecs'
        ]);
      } else {
        if (['PLANNED', 'BASELINE', 'LAST_STOP'].includes(completeBasedOn)) {
          delete values?.completeAfterTimeSecs;
        }
        if (['FIXED', 'BASELINE', 'LAST_STOP'].includes(completeBasedOn)) {
          delete values?.completeAfterPlannedTimeSecs;
        }
        if (['FIXED', 'PLANNED', 'LAST_STOP'].includes(completeBasedOn)) {
          delete values?.completeAfterBaseLineTimeSecs;
        }
      }
    };

    deleteStartFields();
    deleteCompleteFields();

    return values;
  };

  return (
    <BaseCrud
      tabName="routeactionsconfiguration.section.gridindex"
      updateMethod="POST"
      beforeSubmit={beforeSubmit}
      gridCallbacks={{
        beforeList
      }}
      {...props}
    >
      {renderGeneralSection()}
      {renderCompoundRASConfigurationSection(props)}
    </BaseCrud>
  );
}

renderCompoundRASConfigurationSection.propTypes = {
  allowStart: PropTypes.bool,
  allowComplete: PropTypes.bool,
  startBasedOn: PropTypes.string,
  completeBasedOn: PropTypes.string
};

renderCompoundRASConfigurationSection.defaultProps = {
  allowStart: false,
  allowComplete: false,
  startBasedOn: 'FIXED',
  completeBasedOn: 'FIXED'
};

const valueSelector = formValueSelector('RouteActionsConfiguration');

const mapStateToProps = (state) => ({
  allowStart: valueSelector(state, 'allowStart'),
  allowComplete: valueSelector(state, 'allowComplete'),
  startBasedOn: valueSelector(state, 'startBasedOn'),
  completeBasedOn: valueSelector(state, 'completeBasedOn')
});

export default connect(mapStateToProps)(RouteActionsConfigurationCrud);
